import { api, clearToken } from "../api";

export async function loginView(root: HTMLElement) {
  if (typeof __CLOUDPUSH_MODE__ !== "undefined" && __CLOUDPUSH_MODE__ === "local") {
    const { loginLocalView } = await import("./loginLocal");
    return loginLocalView(root);
  }
  clearToken();
  // Single-use setup/recovery credential arrives in a fragment (never server
  // access logs or Referer). Remove it before fetching anything, keep in memory.
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const setupToken = fragment.get("setup");
  if (setupToken) history.replaceState(null, "", window.location.pathname + window.location.search);
  let settingUp = !!setupToken;
  root.innerHTML = `
    <main class="login-screen">
      <section class="login-box card" aria-labelledby="login-title">
        <div class="login-brand"><span class="brand-mark" aria-hidden="true"></span>CloudPush</div>
        <h1 id="login-title">${settingUp ? "Set up your sign-in" : "Welcome back"}</h1>
        <p class="login-intro">${settingUp ? "Choose your owner username and password. Your saved client work and connections stay in place." : "Sign in to your client workspace."}</p>
        <form id="login-form">
          <label for="username">Username</label>
          <input id="username" name="username" autocomplete="username" autocapitalize="none" spellcheck="false" required minlength="3" maxlength="80" />
          <label for="password">Password</label>
          <div class="login-password"><input id="password" name="password" type="password" autocomplete="${settingUp ? "new-password" : "current-password"}" required maxlength="128" ${settingUp ? 'minlength="15"' : ""} /><button type="button" class="secondary" id="show-password" aria-label="Show password" aria-pressed="false">Show</button></div>
          ${settingUp ? '<p class="muted" id="password-hint">Use at least 15 characters. A longer passphrase works well.</p><label for="confirm-password">Confirm password</label><input id="confirm-password" type="password" autocomplete="new-password" required minlength="15" maxlength="128" />' : ""}
          <p id="login-error" role="alert" class="error-banner" hidden></p>
          <p id="login-status" role="status" class="muted"></p>
          <button id="sign-in" type="submit" class="login-submit">${settingUp ? "Save sign-in" : "Sign in"}</button>
        </form>
        <details class="login-help"><summary>Need help signing in?</summary><p>Use your CloudPush username and password. For first-time setup or a forgotten password, ask the workspace owner for a new secure setup link. Your cloud platform keys are separate and do not need to be entered again.</p></details>
      </section>
    </main>`;
  const form = root.querySelector<HTMLFormElement>("#login-form")!;
  const password = form.querySelector<HTMLInputElement>("#password")!;
  const show = form.querySelector<HTMLButtonElement>("#show-password")!;
  show.onclick = () => {
    const visible = password.type === "password";
    password.type = visible ? "text" : "password";
    show.textContent = visible ? "Hide" : "Show";
    show.setAttribute("aria-label", visible ? "Hide password" : "Show password");
    show.setAttribute("aria-pressed", String(visible));
  };
  form.onsubmit = async event => {
    event.preventDefault();
    const error = form.querySelector<HTMLElement>("#login-error")!;
    const status = form.querySelector<HTMLElement>("#login-status")!;
    const button = form.querySelector<HTMLButtonElement>("#sign-in")!;
    if (button.disabled) return;
    error.hidden = true;
    const confirmation = form.querySelector<HTMLInputElement>("#confirm-password");
    if (settingUp && confirmation?.value !== password.value) {
      error.textContent = "The passwords do not match."; error.hidden = false; return;
    }
    button.disabled = true;
    status.textContent = settingUp ? "Saving your sign-in…" : "Signing in…";
    try {
      await api(settingUp ? "/auth/setup" : "/auth/login", { method:"POST", body:JSON.stringify({
        username:form.querySelector<HTMLInputElement>("#username")!.value.trim(), password:password.value,
        ...(settingUp ? {token:setupToken} : {}),
      }) });
      password.value = "";
      if (settingUp) {
        settingUp = false;
        confirmation?.previousElementSibling?.remove(); confirmation?.remove();
        form.querySelector("#password-hint")?.remove();
        password.autocomplete = "current-password";
        root.querySelector("#login-title")!.textContent = "Your sign-in is ready";
        root.querySelector(".login-intro")!.textContent = "Sign in with the username and password you just saved.";
        button.textContent = "Sign in"; status.textContent = "Password saved. Other signed-in devices have been signed out.";
        password.focus();
      } else {
        window.location.hash = "/";
        window.location.reload();
      }
    } catch (e) {
      status.textContent = "";
      error.textContent = e instanceof Error ? e.message : "Could not sign in. Please try again.";
      error.hidden = false;
    } finally { button.disabled = false; }
  };
}
